import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { EstadoPuerta, Puerta } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api } from '../api.js';

/**
 * EL MAPA DE LO RECORRIDO.
 *
 * La pregunta que contesta no es «dónde está esta puerta» sino «qué calles ya
 * hicimos y cómo nos fue». Por eso el color manda sobre todo lo demás: de un
 * vistazo, desde el coche, se tiene que ver dónde abrieron y dónde no.
 *
 * Verde: abrieron y hay algo vivo. Azul: abrieron. Gris: nadie salió. Rojo
 * apagado: descartada. Los cinco estados caben en cinco colores y no hace falta
 * leer ninguna leyenda dos veces.
 */

interface Props {
  puertas: Puerta[];
  seleccionada: string | null;
  idioma: Idioma;
  onSeleccionar: (id: string | null) => void;
  onAbrir: (id: string) => void;
}

const ISLA: L.LatLngBoundsExpression = [
  [39.26, 2.33],
  [39.96, 3.46],
];

const COLOR: Record<EstadoPuerta, string> = {
  ATENDIO: '#1b4fa0',
  NO_ATENDIO: '#8a8a8a',
  SEGUIMIENTO: '#1d6b3d',
  DESCARTADA: '#96231f',
  CLIENTE: '#8a5a12',
};

function dibujo(p: Puerta, elegida: boolean): string {
  const color = COLOR[p.estado];
  const borde = elegida ? '#111' : '#fff';
  const ancho = elegida ? 3 : 2;
  return `<svg width="30" height="30" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
    <circle cx="15" cy="15" r="10" fill="${color}" stroke="${borde}" stroke-width="${ancho}" />
    ${p.fotos.length > 0 ? '<circle cx="23" cy="7" r="4" fill="#fff" stroke="' + color + '" stroke-width="2" />' : ''}
  </svg>`;
}

export function MapaPuertas({ puertas, seleccionada, idioma, onSeleccionar, onAbrir }: Props) {
  const caja = useRef<HTMLDivElement>(null);
  const mapa = useRef<L.Map | null>(null);
  const marcadores = useRef(new Map<string, L.Marker>());
  const encuadrado = useRef(false);
  const acciones = useRef({ onSeleccionar, onAbrir });
  acciones.current = { onSeleccionar, onAbrir };
  const [config, setConfig] = useState<Awaited<ReturnType<typeof api.mapaConfig>> | null>(null);
  /*
    UN CONTADOR, NO UN «ya esta».

    Con un booleano las chinchetas no salian hasta tocar un filtro. En
    desarrollo React monta cada efecto dos veces —crea el mapa, lo destruye y lo
    vuelve a crear— y entre medias el booleano pasaba de true a false y otra vez
    a true: para React eso no es un cambio, asi que el efecto de las chinchetas
    no se volvia a ejecutar y se habian pintado sobre el mapa ya destruido.

    Un contador que sube con cada mapa nuevo siempre es un valor distinto, y
    siempre las vuelve a pintar donde toca.
  */
  const [generacion, setGeneracion] = useState(0);

  useEffect(() => {
    api.mapaConfig().then(setConfig).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!caja.current || !config || mapa.current) return;
    const m = L.map(caja.current, {
      zoomControl: true,
      scrollWheelZoom: false,
      maxBounds: ISLA,
      maxBoundsViscosity: 0.4,
    }).fitBounds(ISLA);

    const calle = L.tileLayer(config.calle.url, {
      attribution: config.calle.atribucion,
      maxZoom: config.calle.zoomMaximo,
    }).addTo(m);

    if (config.satelite) {
      const satelite = L.tileLayer(config.satelite.url, {
        attribution: config.satelite.atribucion,
        maxZoom: config.satelite.zoomMaximo,
      });
      L.control
        .layers(
          { [t(idioma, 'obras.mapaCalle')]: calle, [t(idioma, 'obras.mapaSatelite')]: satelite },
          {},
          { position: 'topright' },
        )
        .addTo(m);
    }

    m.on('click', () => acciones.current.onSeleccionar(null));
    mapa.current = m;
    setGeneracion((g) => g + 1);

    /*
      El mapa mide lo que mida su caja, y la caja cambia: nace mientras la
      pantalla se coloca y se queda con el tamaño viejo si nadie le avisa.
    */
    const observador = new ResizeObserver(() => m.invalidateSize());
    observador.observe(caja.current);

    return () => {
      observador.disconnect();
      m.remove();
      mapa.current = null;
      marcadores.current.clear();
      encuadrado.current = false;
    };
  }, [config, idioma]);

  useEffect(() => {
    const m = mapa.current;
    if (!m) return;
    const vistos = new Set<string>();

    for (const p of puertas) {
      if (!Number.isFinite(p.lat) || !Number.isFinite(p.lon)) continue;
      if (p.lat === 0 && p.lon === 0) continue;
      vistos.add(p.id);
      const elegida = p.id === seleccionada;
      const icono = L.divIcon({
        html: dibujo(p, elegida),
        className: 'chincheta-puerta',
        iconSize: [30, 30],
        iconAnchor: [15, 15],
      });
      const existente = marcadores.current.get(p.id);
      if (existente) {
        existente.setLatLng([p.lat, p.lon]).setIcon(icono);
        existente.setZIndexOffset(elegida ? 1000 : 0);
        continue;
      }
      const marca = L.marker([p.lat, p.lon], {
        icon: icono,
        title: p.direccion,
        alt: p.direccion,
        keyboard: true,
        zIndexOffset: elegida ? 1000 : 0,
      }).addTo(m);
      // El primer toque la elige; el segundo abre su ficha.
      marca.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        const abierta = marcadores.current.get(p.id)?.options.zIndexOffset === 1000;
        if (abierta) acciones.current.onAbrir(p.id);
        else acciones.current.onSeleccionar(p.id);
      });
      marcadores.current.set(p.id, marca);
    }

    for (const [id, marca] of marcadores.current) {
      if (vistos.has(id)) continue;
      marca.remove();
      marcadores.current.delete(id);
    }

    /*
      Se encuadra UNA vez, sobre lo que haya al abrir. Reencuadrar en cada
      filtrado haría saltar el mapa al tocar una pastilla, y se perdería la
      calle que uno acaba de buscar a mano.
    */
    if (!encuadrado.current && vistos.size > 0 && (caja.current?.clientWidth ?? 0) > 80) {
      const puntos = puertas
        .filter((p) => vistos.has(p.id))
        .map((p) => [p.lat, p.lon] as [number, number]);
      m.fitBounds(L.latLngBounds(puntos).pad(0.25), { maxZoom: 17 });
      encuadrado.current = true;
    }
  }, [puertas, seleccionada, generacion]);

  return (
    <div className="mapa-marco">
      <div
        ref={caja}
        className="mapa-teselas"
        role="application"
        aria-label={t(idioma, 'pap.mapa')}
      />
      <div className="pap-leyenda">
        {(['SEGUIMIENTO', 'ATENDIO', 'NO_ATENDIO', 'CLIENTE', 'DESCARTADA'] as EstadoPuerta[]).map(
          (e) => (
            <span key={e}>
              <i style={{ background: COLOR[e] }} aria-hidden="true" />
              {t(idioma, `pap.estado.${e}`)}
            </span>
          ),
        )}
      </div>
    </div>
  );
}
